import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Booking } from '../booking';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';
import { isGatePassSuccess, getGatePassErrorMessage } from '../../../../core/utils/gatePassUtils';

// SweetAlert2 utils (unchanged) ...
export const showCancelConfirmation = (onConfirm: () => void) => { /* ... */ };
export const showError = (message: string) => { /* ... */ };

// Inline styles (unchanged) ...
const style = document.createElement('style');
style.textContent = `/* ... */`;
document.head.appendChild(style);

interface OngoingBookingsModalProps {
  show: boolean;
  onHide: () => void;
  OngoingBookingsDetails: Booking[];
  OngoingBookingsModalError: string | null;
  handleViewClick: (booking: Booking) => void;
  handleEditClick: (booking: Booking) => void;
  statusColors: { [key: string]: string };
}

const currentUser = {
  empNumber: localStorage.getItem('username') || localStorage.getItem('empNumber') || 'unknown',
  roles: JSON.parse(localStorage.getItem('roles') || '[]'),
  group: localStorage.getItem('group') || null,
};

const canUpdateBooking = (booking: Booking): boolean => {
  const isCreator = booking.createdBy === currentUser.empNumber;
  const isServiceManager = currentUser.group?.includes('SERVICE_MANAGER');
  const isDepartmentHead = currentUser.group?.includes('DEPARTMENT_HEAD');
  const bookingStatus = booking.status?.toUpperCase() || 'UNKNOWN';

  if (['COMPLETED', 'MERGED', 'EXPIRED'].includes(bookingStatus)) return false;
  if (isServiceManager && ['PENDING', 'APPROVED', 'FINAL_APPROVED'].includes(bookingStatus)) return true;
  if (isDepartmentHead && ['PENDING', 'APPROVED'].includes(bookingStatus)) return true;
  if (isCreator && bookingStatus === 'PENDING') return true;
  return false;
};

// Treat any of these as ongoing
const isOngoingStatus = (status?: string | null) => {
  const s = (status || '').toUpperCase();
  return ['ONGOING', 'ON_GOING', 'IN_PROGRESS'].includes(s);
};

// 🚫 NEW: Only SERVICE_MANAGER can see Mark In
const canSeeMarkIn = (booking: Booking) => {
  const isServiceManager = currentUser.group?.includes('SERVICE_MANAGER');
  return isServiceManager && isOngoingStatus(booking.status);
};

interface DriverLite {
  empNumber: string;
  name: string;
}
interface VehicleLite {
  numberPlate: string;
  model: string;
}
interface MarkInFormData {
  vehicleNumber: string;
  inDriverId: string;
  kmsIn: number;
  comment: string;
}

const OngoingBookingsModal: React.FC<OngoingBookingsModalProps> = ({
  show,
  onHide,
  OngoingBookingsDetails,
  OngoingBookingsModalError,
  handleViewClick,
  handleEditClick,
  statusColors,
}) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalBookings, setTotalBookings] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const pageSize = 15;

  // Cancel flow (unchanged)
  const [cancelReason, setCancelReason] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

  // ✅ MARK-IN state
  const [showMarkInModal, setShowMarkInModal] = useState<boolean>(false);
  const [markInForm, setMarkInForm] = useState<MarkInFormData>({
    vehicleNumber: '',
    inDriverId: '',
    kmsIn: 0,
    comment: '',
  });
  const [markInBookingId, setMarkInBookingId] = useState<number | null>(null);
  const [drivers, setDrivers] = useState<DriverLite[]>([]);
  const [vehicles, setVehicles] = useState<VehicleLite[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState<boolean>(false);
  const [loadingVehicles, setLoadingVehicles] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      setCurrentPage(1);
      fetchBookings(1);
    }
  }, [show]);

  const fetchBookings = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiDefinitions.getOngoingVehicleBookings();
      const responseData = response.data;
      if (responseData.status === 'S1000' && responseData.data.vehicleBookingRequests) {
        setBookings(responseData.data.vehicleBookingRequests);
        setTotalBookings(responseData.data.total || responseData.data.vehicleBookingRequests.length);
      } else {
        setBookings([]);
        setTotalBookings(0);
        setError(responseData.statusDescription || 'No bookings available.');
      }
    } catch (err: any) {
      setBookings([]);
      setTotalBookings(0);
      setError(err.response?.data?.statusDescription || 'Failed to fetch bookings: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // === MARK-IN helpers ===
  const fetchDriversLite = async () => {
    setLoadingDrivers(true);
    try {
      const payload = { filterType: null, filterValue: '', date: null };
      const res = await apiDefinitions.getUsersByType('DRIVER', payload);
      if (res.data?.status === 'S1000') {
        const list = Array.isArray(res.data?.data?.users) ? res.data.data.users : [];
        const mapped: DriverLite[] = list
          .map((u: any) => ({ empNumber: String(u.empNumber || ''), name: u.name || 'Unknown' }))
          .filter((d: DriverLite) => d.empNumber);
        setDrivers(mapped);
      } else {
        setDrivers([]);
        Swal.fire({ icon: 'warning', title: 'Drivers', text: res.data?.statusDescription || 'Failed to load drivers.' });
      }
    } catch (e: any) {
      setDrivers([]);
      Swal.fire({
        icon: 'error',
        title: 'Drivers Error',
        text: e?.response?.data?.statusDescription || e?.message || 'Failed to load drivers.',
      });
    } finally {
      setLoadingDrivers(false);
    }
  };

  // 🔎 Fetch vehicles for THIS booking using your existing API pattern
  const fetchVehiclesForBooking = async (booking: Booking) => {
  setLoadingVehicles(true);

  try {
    // Fetch all vehicles
    const vehicleRes = await apiDefinitions.getVehicles(1);

    let availableVehicles: VehicleLite[] = [];

    if (vehicleRes.data.status === 'S1000') {
      availableVehicles = (vehicleRes.data.data || [])
        .filter((v: any) => v.vehicleStatus === 'AVAILABLE')
        .map((v: any) => ({
          numberPlate: String(v.numberPlate || ''),
          model: v.vehicleModel || 'Unknown',
          noOfPassengers: Number(v.noOfPassengers) || 0,
        }))
        .filter((v: VehicleLite) => v.numberPlate); // Ensure no empty plates
    }

    // 🚀 Always add booking's currently assigned vehicle
    if (booking.vehicle?.numberPlate) {
      const currentVehicle = {
        numberPlate: booking.vehicle.numberPlate,
        model: booking.vehicle.model || 'Assigned Vehicle',
        noOfPassengers: booking.vehicle.noOfPassengers || 0,
      };

      // Avoid duplicates
      const exists = availableVehicles.some(
        v => v.numberPlate === currentVehicle.numberPlate
      );

      if (!exists) {
        availableVehicles.unshift(currentVehicle);
      }
    }

    setVehicles(availableVehicles);
  } catch (e: any) {
    setVehicles([]);
    Swal.fire({
      icon: 'error',
      title: 'Vehicle Fetch Error',
      text:
        e?.response?.data?.statusDescription ||
        e?.message ||
        'Failed to fetch vehicles.',
    });
  } finally {
    setLoadingVehicles(false);
  }
};


  const openMarkInModal = async (booking: Booking) => {
    setMarkInBookingId(booking.id);
    setMarkInForm({
  vehicleNumber: booking.vehicle?.numberPlate || '',
  inDriverId: booking.outDriverId || '',    // correct field
  kmsIn: booking.mileageOut ? booking.mileageOut + 1 : 0,  
  comment: '',
});

    await Promise.all([fetchDriversLite(), fetchVehiclesForBooking(booking)]);
    setShowMarkInModal(true);
  };

  const closeMarkInModal = () => {
    setShowMarkInModal(false);
    setMarkInBookingId(null);
    setMarkInForm({ vehicleNumber: '', inDriverId: '', kmsIn: 0, comment: '' });
    setVehicles([]);
  };

  const handleMarkInInputChange = (field: keyof MarkInFormData, value: string | number) => {
    setMarkInForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMarkInSubmit = async () => {
    if (!markInBookingId) {
      showError('No booking selected for Mark In.');
      return;
    }
    if (!markInForm.vehicleNumber || !markInForm.inDriverId || markInForm.kmsIn <= 0 || !markInForm.comment.trim()) {
      showError('Please fill all Mark In fields (vehicle, driver, kilometers, comment).');
      return;
    }

    const payload = {
      vehicleBookingRequestId: markInBookingId,
      vehicleNumber: markInForm.vehicleNumber,
      inDriverId: markInForm.inDriverId,
      kmsIn: markInForm.kmsIn,
      comment: markInForm.comment,
    };

    try {
      const res = await apiDefinitions.markVehicleIn(payload);
      if (isGatePassSuccess(res)) {
        Swal.fire({
          icon: 'success',
          title: 'Vehicle Marked In',
          text: `Booking ${markInBookingId} successfully marked in.`,
          customClass: { popup: 'small-swal' },
        });
        closeMarkInModal();
        fetchBookings(currentPage);
      } else {
        const msg = res.data?.statusDescription || 'Failed to mark vehicle in.';
        showError(msg);
      }
    } catch (e: any) {
      const msg = getGatePassErrorMessage(e, 'Failed to mark vehicle in.');
      showError(msg);
    }
  };

  // pagination handlers (unchanged) ...
  const handleNextPage = () => { /* ... */ };
  const handlePreviousPage = () => { /* ... */ };

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" aria-labelledby="all-bookings-modal">
        <Modal.Header closeButton>
          <Modal.Title id="all-bookings-modal">Ongoing Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {OngoingBookingsModalError && <div className="alert alert-danger">{OngoingBookingsModalError}</div>}
          {loading ? (
            <div>Loading bookings...</div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : bookings.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Emp Num</th>
                    <th>Name</th>
                    <th>Vehicle</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.id}</td>
                      <td>{booking.user?.empNumber || 'N/A'}</td>
                      <td>{booking.user?.name || 'N/A'}</td>
                      <td>{booking.vehicle?.numberPlate || 'N/A'}</td>
                      <td>
                        <span className={statusColors[booking.status] || 'badge bg-secondary'}>
                          {booking.status || 'N/A'}
                        </span>
                      </td>
                      <td>{booking.priority || 'N/A'}</td>
                      <td>{new Date(booking.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="hstack gap-2 fs-3">
                          <button
                            className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                            onClick={() => handleViewClick(booking)}
                            title="View Booking"
                          >
                            <i className="feather-eye" />
                          </button>

                          {canUpdateBooking(booking) && (
                            <button
                              className="btn-icon btn-sm btn-soft-info rounded-pill"
                              onClick={() => handleEditClick(booking)}
                              title="Edit Booking"
                            >
                              <i className="feather-edit" />
                            </button>
                          )}

                          {/* ✅ NEW: Mark In visible ONLY to SERVICE_MANAGER and when ongoing */}
                          {canSeeMarkIn(booking) && (
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => openMarkInModal(booking)}
                              title="Mark Vehicle In"
                            >
                              Mark In
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-primary" onClick={handlePreviousPage} disabled={currentPage === 1 || loading}>
                  Previous
                </Button>
                <span>Page {currentPage} of {Math.ceil(totalBookings / pageSize) || 1}</span>
                <Button
                  variant="outline-warning"
                  onClick={handleNextPage}
                  disabled={currentPage >= Math.ceil(totalBookings / pageSize) || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : (
            <p>No bookings available.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel booking modal (unchanged) ... */}

      {/* ✅ NEW: Mark In modal */}
      <Modal show={showMarkInModal} onHide={closeMarkInModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mark Vehicle In {markInBookingId ? `for Booking #${markInBookingId}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Vehicle from API (for this booking) */}
            <Form.Group className="mb-3">
              <Form.Label>Vehicle</Form.Label>
              <Form.Select
                value={markInForm.vehicleNumber}
                onChange={(e) => handleMarkInInputChange('vehicleNumber', e.target.value)}
                disabled={loadingVehicles}
              >
                <option value="">Select Vehicle</option>
                {vehicles.map((v) => (
                  <option key={v.numberPlate} value={v.numberPlate}>
                    {v.model} ({v.numberPlate})
                  </option>
                ))}
              </Form.Select>
              {loadingVehicles && <Form.Text>Loading vehicles…</Form.Text>}
              {!loadingVehicles && !markInForm.vehicleNumber }
            </Form.Group>

            {/* Driver list */}
            <Form.Group className="mb-3">
              <Form.Label>Driver</Form.Label>
              <Form.Select
                value={markInForm.inDriverId}
                onChange={(e) => handleMarkInInputChange('inDriverId', e.target.value)}
                disabled={loadingDrivers}
              >
                <option value="">Select Driver</option>
                {drivers.map((d) => (
                  <option key={d.empNumber} value={d.empNumber}>
                    {d.name} ({d.empNumber})
                  </option>
                ))}
              </Form.Select>
              {loadingDrivers && <Form.Text>Loading drivers…</Form.Text>}
              {!loadingDrivers && !markInForm.inDriverId}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Kilometers In</Form.Label>
              <Form.Control
                type="number"
                value={markInForm.kmsIn}
                onChange={(e) => handleMarkInInputChange('kmsIn', parseInt(e.target.value) || 0)}
                placeholder="Enter current odometer reading"
                min="0"
              />
              {markInForm.kmsIn <= 0 }
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comment</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={markInForm.comment}
                onChange={(e) => handleMarkInInputChange('comment', e.target.value)}
                placeholder="Enter comment (e.g., vehicle returned after trip)"
              />
              {!markInForm.comment.trim()}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeMarkInModal}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleMarkInSubmit}
            disabled={
              !markInForm.vehicleNumber ||
              !markInForm.inDriverId ||
              markInForm.kmsIn <= 0 ||
              !markInForm.comment.trim()
            }
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OngoingBookingsModal;